import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { ErrorService, LayoutService } from '@core/services';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { finalize, takeUntil } from 'rxjs/operators';
import { SelectionOption, StiiraError } from '@core/models';
import { ScheduleInfoDialogPost, ScheduleInfoForm, WorkScheduleDetails, WorkScheduleInfoDialog } from '@core/models/work-schedules/work-schedule-details.model';
import { WorkSchedulesService } from '@core/services/work-schedules.service';
import { WorkScheduleDetailsStoreService } from '@core/services/work-schedule-details.store.service';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { DayOfWeek } from '@core/enums';
import { isNumber } from '@microsoft/applicationinsights-core-js';

@Component({
    selector: 'app-edit-schedule-info',
    templateUrl: './edit-schedule-info.component.html',
    styleUrl: './edit-schedule-info.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditScheduleInfoComponent implements OnInit{
  static readonly componentName = 'EditScheduleInfoComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  public form: FormGroup<ScheduleInfoForm>;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean>;
  public isMobile$: Observable<boolean>;
  public position = position;
  public dayOfWeekOptions: SelectionOption[];
  public onDragEnded = onDragEnded;

  private formChangeEmitted: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();
  private dayOfWeek = DayOfWeek;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { 
      workScheduleDetails: WorkScheduleDetails;
      options: WorkScheduleInfoDialog;
      sysText: any; 
      unsavedChangesSysText: any
    },
    private templateInfoDialogRef: MatDialogRef<EditScheduleInfoComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private service: WorkSchedulesService,
    private store: WorkScheduleDetailsStoreService,
    private errorService: ErrorService,
    private layoutService: LayoutService
    ) { 
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group<ScheduleInfoForm>({
      scheduleName: this.fb.control(null, Validators.required),
      ownerEmployerId: this.fb.control(null),
      nonWorkdays: this.fb.control(null),
      allowUse: this.fb.control(null),
      startOfWorkWeek: this.fb.control(null)
    })
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();
    closeDialogOnEscape(this.templateInfoDialogRef, this.isSubmitting$, () => this.cancel());

    this.dayOfWeekOptions = this.buildDayOfWeekOptions();

    this.form.patchValue({
      scheduleName: this.data.workScheduleDetails.name,
      ownerEmployerId: this.data.workScheduleDetails.ownerEmployerId,
      nonWorkdays: this.data.workScheduleDetails.nonWorkdays.map(er => er.id as number),
      allowUse: this.data.workScheduleDetails.allowUse,
      startOfWorkWeek: this.data.workScheduleDetails.startOfWorkweek
    });

    if (!this.data.options.canEditName) {
      this.form.controls.scheduleName.disable();
      this.form.controls.nonWorkdays.disable();
    }

    if (!this.data.options.canEditOwner) {
      this.form.controls.ownerEmployerId.disable();
    }

    if (!this.data.options.canEditAllowUse) {
      this.form.controls.allowUse.disable();
    }

    this.formInitValues = { ...this.form.value };
    this.setFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setFormErrors() {
    setTimeout(() => {
      this.errorService.setFormModelStateErrors(this.form, this.data.workScheduleDetails.missingDetails)
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  public save(): void {
    const dto: ScheduleInfoDialogPost = {
      scheduleId: this.data.workScheduleDetails.id,
      scheduleName: this.form.controls.scheduleName.value,
      nonWorkdays: this.form.controls.nonWorkdays.value,
      ownerEmployerId: this.form.controls.ownerEmployerId.value,
      allowUse: this.form.controls.allowUse.value,
      startOfWorkweek: this.form.controls.startOfWorkWeek.value
    }
      this.service.postScheduleInfo(dto)
        .pipe(
          indicate(this.isSubmitting$),
          finalize(() => { })
        )
        .subscribe((res) => {
          this.store.workScheduleDetails = res;
          this.templateInfoDialogRef.close(res);
        }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
  }

  public cancel(): void {
    if (this.noChanges) {
      this.templateInfoDialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  public openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.templateInfoDialogRef.close();
        }
      });
  }

  private buildDayOfWeekOptions(): SelectionOption[] {
    const dayOfWeekIds = Object.values(this.dayOfWeek).filter(v => isNumber(v));
    let dayOfWeekOptions: SelectionOption[] = [];

    dayOfWeekIds.forEach(id => {
      const desc: string = this.dayOfWeek[id];
      const option: SelectionOption = {
        id: id,
        description: desc
      };
      dayOfWeekOptions.push(option);
    });

    return dayOfWeekOptions;
  }
}
